import { Injectable, ViewChild } from "@angular/core";
import { AlertInput, IonContent } from "@ionic/angular";
import { Subscription } from "rxjs";
import * as dayjs from "dayjs";

import { environment } from "src/environments/environment";
import { ModalType } from "src/app/app.constants";
import {
	FeedState,
	MilkState,
	ScanLabelTitle,
	ScanLogType,
	ScanLogWorkflow,
	ScanType,
} from "src/app/app.enums";
import {
	capitalize,
	displayContents,
	displayDate,
	getCaloricDensityText,
	getMaxDate,
	getMinDate,
	getMinDateString,
	getRelativeFeedingTime,
} from "src/app/app.util";

import { MilkBottleModel } from "src/app/models/milk.model";
import { MilkBankProductModel } from "src/app/models/milk-bank-product.model";
import { PatientModel } from "src/app/models/patient.model";
import { RecipeData } from "src/app/models/recipe.model";

import { AuthGuardService } from "src/app/services/guards/auth-guard.service";
import { BaseService } from "src/app/services/base.service";
import {
	IWristband,
	ScannedObject,
	ScanningService,
} from "src/app/services/scanning.service";
import { StorageService } from "src/app/services/storage.service";

import { SelectPatientModal } from "./modals/select-patient/select-patient.modal";
import { getWristbandParams } from "./services/patient.service";
import {
	getRelativeExpirationTime,
	isExpired,
	isSoonerExpiration,
} from "./utils/expiration.util";
import { DebugSelectMilkModal } from "./modals/debug-select-milk/debug-select-milk.modal";
import { MilkModifierEvent } from "./components/milk-modifiers/milk-modifiers.component";
import {
	displayAddedContents,
	getBottleNumberText,
	getFeedTypeText,
	getMilkTypeText,
	getStatusColor,
	hasFortified,
	hasModifiersSelected,
	hasProductModifiersSelected,
} from "./utils/milk-label.util";
import { EditMilkModal } from "./modals/edit-milk/edit-milk.modal";
import { filter } from "rxjs/operators";
import { Nurse } from "./components/nurse-info/nurse-info.component";
import {
	LogAttemptModel,
	LogMilkBankProductModel,
	LogMilkBottleModel,
	LogScannedObjectModel,
} from "./models/log-scan.model";
import {
	faCalendarDays,
	faChevronRight,
	faJar,
	faUserPlus,
	faUtensils,
	faWarning,
} from "@fortawesome/free-solid-svg-icons";
import {
	ScanMilkBottleValidationError,
	BottleNotReceivedError,
	ScanMilkBankProductValidationError,
	ScannedObjectError,
	ScannedPatientError,
	ScannedWristbandError,
} from "./decorators/scan-validation/scan-validation-error";
import {
	CheckPatientMatch,
	RejectExpired,
	RejectRecalledProduct,
} from "./decorators/scan-validation";
import { isDischarged } from "./utils/inventory.util";
import { modalMessage } from "./app.modal-messages";
import { FeedObject } from "./models/feed-object.model";
import { SelectItemModal } from "./modals/select-item/select-item.modal";
import { PrinterModel } from "./models/printer.model";
import { RejectDuplicatePatient } from "./decorators/patient-validation/duplicate-patient.decorator";
import { FeatureFlagService } from "./services/feature-flag.service";
import { Features } from "./features.enums";
import { ErrorModel } from "./models/error.model";
import { IAuthorizedTenant } from "./models/user-profile.model";

@Injectable()
export abstract class BasePage {
	@ViewChild(IonContent) content: IonContent;

	MilkState = {
		Fresh: MilkState.Fresh,
		Frozen: MilkState.Frozen,
		Thawed: MilkState.Thawed,
		Stable: MilkState.Stable,
		Opened: MilkState.Opened,
	};

	isDebug: boolean;
	isWeb: boolean;

	scanSub: Subscription;
	ScanType = {
		Wristband: ScanType.Wristband,
		MilkLabel: ScanType.MilkLabel,
		FactoryDonorLabel: ScanType.Unreceived_Product,
	};
	scannedObjects: FeedObject[] = []; // Displays in multi-scanner

	defaultPrintQty = 1;
	labelQuantity: number = this.defaultPrintQty;

	/*
	 	Declaring as null is used to differentiate from undefined. This is used
	 	to determine if the patient has been selected as well as if a patient
	 	needs to be selected. See `hasSelectMilkPermission` for more details.
	 */
	// /**
	//  * @deprecated Since certain workflows like {@link LinkSiblings} allow for
	//  * scanning multiple patients, this should be replaced with an array of
	//  * {@link PatientModel} so we don't have two implementations of the same
	//  * functionality.
	//  */
	// patient: PatientModel = null;
	patients: PatientModel[] = [];

	assignedPatients: PatientModel[] = []; // IPatient or ISibling

	/**
	 * This stores all the scanned objects (milk bottles, milk bank products,
	 * and products).
	 */
	feedObjects: FeedObject[] = [];

	/**
	 * This should store the serialized (stringified) version of the original milk bottles.
	 */
	originalFeedObjects: string[] = [];

	/**
	 * @deprecated should be replaced with originalFeedObjects
	 * This should store the serialized (stringified) version of the original milk bottles.
	 */
	originalMilks: string[] = [];

	/**
	 * @deprecated should be replaced with feedObjects
	 */
	milkBottles: MilkBottleModel[] = [];

	/**
	 * @deprecated should be replaced with feedObjects
	 */
	milkBankProducts: MilkBankProductModel[] = [];

	/**
	 * Some workflows show patient inventory in order for the user to check the
	 * available inventory.
	 */
	inventory: MilkBottleModel[] = [];

	recipeData: RecipeData = null;
	scannedProducts: MilkBankProductModel[] = [];

	options: ScanOptions;

	selectedPrinter: PrinterModel;
	preferredTenant: IAuthorizedTenant;

	workflow: ScanLogWorkflow;

	state = 1;

	environment = environment;
	Features = Features;

	// Pass in util functions to call in HTML
	capitalize = capitalize;
	displayAddedContents = displayAddedContents;
	displayContents = displayContents;
	getBottleNumberText = getBottleNumberText;
	getCaloricDensityText = getCaloricDensityText;
	getFeedTypeText = getFeedTypeText;
	getMinDate = getMinDate;
	getMaxDate = getMaxDate;
	getMilkTypeText = getMilkTypeText;
	getMinDateString = getMinDateString;
	getRelativeExpirationTime = getRelativeExpirationTime;
	getRelativeFeedingTime = getRelativeFeedingTime;
	getStatusColor = getStatusColor;
	hasFortified = hasFortified;
	hasModifiersSelected = hasModifiersSelected;
	hasProductModifiersSelected = hasProductModifiersSelected;
	isExpired = isExpired;
	isSoonerExpiration = isSoonerExpiration;
	StorageService = StorageService;
	FeatureFlagService = FeatureFlagService;

	// Icons
	faCalendarDays = faCalendarDays;
	faChevronRight = faChevronRight;
	faUserPlus = faUserPlus;
	faUtensils = faUtensils;
	faWarning = faWarning;
	faJar = faJar;

	protected constructor(
		protected base: BaseService,
		protected scanningService: ScanningService
	) {
		this.isDebug = !environment.production;
		this.isWeb = StorageService.isWeb;
	}

	ionViewDidEnter() {
		this.selectedPrinter = StorageService.lastUsedCachedPrinter;
		this.preferredTenant = StorageService.preferredTenant;
		this.base.listenForInternetConnection();
	}

	/*
	 TODO: all Pages should use ionViewWillLeave instead of ngOnDestroy, which isn't called all the time.
	 https://forum.ionicframework.com/t/ionic-4-ngondestroy-not-called-from-component-on-page-navigation/170704/4
	 https://ionicframework.com/docs/angular/lifecycle
	 */
	ionViewWillLeave() {
		this.base.clean();
	}

	// Navigation

	async prev() {
		await this.beforePrev();

		if (this.state <= 1) {
			if (
				this.milkBottles.length ||
				this.milkBankProducts.length ||
				this.feedObjects.length
			) {
				this.presentLeavingAlert();
			} else {
				this.base.pop();
			}
		} else {
			this.state--;
		}
		this.afterPrev();
	}

	async beforePrev() {
		// do nothing
	}

	afterPrev() {
		// do nothing
	}

	next() {
		this.beforeNext();
		this.state++;
		this.afterNext();
	}

	beforeNext() {
		// do nothing
	}

	afterNext() {
		// do nothing
	}

	async logout() {
		await this.base.logout();
	}

	scrollToTop() {
		this.content.scrollToTop();
	}

	scrollToBottom() {
		this.content.scrollToBottom(1500);
	}

	async presentLeavingAlert() {
		await this.base.alerts.presentAlert({
			header: "Leaving Workflow",
			message: "Progress on this page will not be saved.",
			backdropDismiss: false,
			buttons: [
				{
					text: "Cancel",
					role: "cancel",
				},
				{
					text: "Leave",
					handler: () => {
						this.handleLeaving();
					},
				},
			],
		});
	}

	handleLeaving() {
		this.originalMilks = [];
		this.milkBottles = [];
		this.milkBankProducts = [];
		this.feedObjects = [];
		this.originalFeedObjects = [];
		this.scannedObjects = [];
		this.base.pop();
	}

	async handleToggleMenu(isOpen) {
		if (isOpen) {
			await this.base.menuCtrl.open();
		} else {
			await this.base.menuCtrl.close();
		}
		StorageService.isMenuOpen = isOpen;
	}

	// Scanning

	/**
	 * This is the most recent implementation.
	 *
	 * This is designed to be ScanType agnostic and it offloads the responsibility of implementation
	 * to handleScannedObject.
	 */
	startScanListener(): Subscription {
		return StorageService.addSubscription(
			this.scanningService.startScanListener4().subscribe(async (o) => {
				await this.handleScannedObject(o);
			})
		);
	}

	stopScanListener() {
		this.scanningService.stopScanListener();
	}

	// Scan processing

	setScanOptions(): void {
		const items: { title: ScanLabelTitle; isVisible: boolean }[] = [];
		const patientWorkflows: ScanLogWorkflow[] = [
			ScanLogWorkflow.Feeding,
			ScanLogWorkflow.Patient_Inventory_Report,
			ScanLogWorkflow.Feeding_Report,
			ScanLogWorkflow.Receive_Milk,
			ScanLogWorkflow.Discharge_Milk,
			ScanLogWorkflow.Thaw_Patients_Milk,
			ScanLogWorkflow.Prep_Feed,
			ScanLogWorkflow.Divide_Feed,
			ScanLogWorkflow.Prep_Formula,
			ScanLogWorkflow.Milk_Room_Prep,
			ScanLogWorkflow.Assign_Product,
			ScanLogWorkflow.Verify_Milk,
			ScanLogWorkflow.Print_Parent,
			ScanLogWorkflow.Print_Fresh,
			ScanLogWorkflow.Print_Custom_Label,
			ScanLogWorkflow.Link_Siblings,
			ScanLogWorkflow.Freeze_Parents_Milk,
			ScanLogWorkflow.Edit_Reprint_Label,
			ScanLogWorkflow.Oral_Care,
		];
		const labelWorkflows: ScanLogWorkflow[] = [
			ScanLogWorkflow.Feeding,
			ScanLogWorkflow.Receive_Milk,
			ScanLogWorkflow.Discharge_Milk,
			ScanLogWorkflow.Thaw_Product,
			ScanLogWorkflow.Thaw_Patients_Milk,
			ScanLogWorkflow.Prep_Feed,
			ScanLogWorkflow.Divide_Feed,
			ScanLogWorkflow.Milk_Room_Prep,
			ScanLogWorkflow.Assign_Product,
			ScanLogWorkflow.Select_Product,
			ScanLogWorkflow.Verify_Milk,
			ScanLogWorkflow.Freeze_Parents_Milk,
			ScanLogWorkflow.Oral_Care,
		];
		const productWorkflows: ScanLogWorkflow[] = [
			ScanLogWorkflow.Receive_Product,
		];
		if (patientWorkflows.includes(this.workflow)) {
			items.push({
				title: ScanLabelTitle.Patient,
				isVisible:
					!this.patients.length ||
					this.workflow ===
						ScanLogWorkflow.Patient_Inventory_Report ||
					this.workflow === ScanLogWorkflow.Feeding_Report ||
					this.workflow === ScanLogWorkflow.Link_Siblings,
			});
		}
		if (labelWorkflows.includes(this.workflow)) {
			items.push({
				title: ScanLabelTitle.Label,
				isVisible:
					(this.patients.length && !this.recipeData) ||
					this.workflow === ScanLogWorkflow.Thaw_Product,
			});
		}
		if (productWorkflows.includes(this.workflow)) {
			items.push({ title: ScanLabelTitle.Product, isVisible: true });
		}
		this.options = {
			isWeb: this.isWeb,
			currentStep: this.state === 1,
			items: items,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async handleScannedObject(scannedObject: ScannedObject) {
		this.setScanOptions();
		console.warn(
			`${this.constructor.name}.handleScannedObject not implemented.`
		);
	}

	handleInputFocus(event: "focus" | "blur") {
		if (event === "focus") {
			this.stopScanListener();
		} else {
			this.startScanListener();
		}
	}

	/**
	 * Used in Milk Room Prep
	 *
	 * On dismissal of the modal, the wristband info (mrn) is passed to
	 * handleSelectedPatient2 which gets the patient details from the server.
	 *
	 * By default, selecting/scanning multiple patients will simply replace the
	 * existing patient in the {@link patients} array.
	 */
	async handleSelectPatientPressed2(options?: IPatientValidationOptions) {
		const modal = await this.base.modalService.presentModal({
			component: SelectPatientModal,
			cssClass: ModalType.SelectPatientList,
		});

		modal.onDidDismiss().then((data) => {
			const wristband = data.data;
			if (wristband) {
				this.handleWristband(wristband, options);
			} else {
				this.startScanListener();
			}
		});

		return await modal.present().then(() => {
			this.stopScanListener();
		});
	}

	/**
	 * The default behavior of handling a scanned wristband is to check if the
	 * assigned feeds were already scanned and in the queue and only allow the
	 * patient to be changed/set if there are no assigned feeds in the queue.
	 *
	 * @param wristband
	 * @param feedObjects
	 */
	async handleWristbandScanned(
		wristband: IWristband,
		feedObjects: FeedObject[],
		options?: IPatientValidationOptions
	) {
		const hasAssignedFeeds = feedObjects.some(
			(feedObject) => feedObject instanceof MilkBottleModel
		);

		/**
		 * This currently is designed to do the same thing as {@link RejectPatientChange2}
		 * According to ML-1809, once any feed object is scanned and added to the queue,
		 * then the patient cannot be changed.
		 */
		if (hasAssignedFeeds) {
			const { header, message, modalType } =
				modalMessage.patient_change_disabled();
			await this.base.modalService.presentOverlayModal(
				modalType,
				header,
				[message]
			);
			return;
		}
		await this.handleWristband(wristband, options);
	}

	/**
	 * Used in Milk Room Prep
	 *
	 * Most workflows only allow for scanning one patients.
	 */
	async handleWristband(
		wristband: IWristband,
		options?: IPatientValidationOptions
	) {
		try {
			const wristbandParams = getWristbandParams(wristband);
			const p =
				await this.base.patientService.getPatient(wristbandParams);
			await this.handlePatientValidation(
				{
					patient: p,
					patients: this.patients,
					feedObjects: this.feedObjects,
				},
				options
			);
		} catch (error) {
			console.error(error);

			// If validation fails in {@link handlePatientValidation}, the error
			// will be thrown and caught here.
			if (error instanceof ScannedWristbandError) {
				await this.handlePatientValidationError({ error });
				return;
			}

			// the global network error handler in TokenInterceptor should display messages for errors besides 404
			// TODO handle 403
			if (error?.status === 404 || error?.status === 403) {
				await this.base.modalService.presentPatientNotFoundModal({
					mrn: wristband.value, // This can be any id (mrn, ecd, uid)
					tenant: StorageService.activeTenant,
					workflow: this.workflow,
				});
				return;
			}

			throw error;
		}
	}

	/**
	 * Runs validations on the patient using decorators.
	 * On successful validation, handle the patient accordingly by adding it to
	 * the {@link patients} array.
	 *
	 * @param params
	 * @param options
	 */
	@RejectDuplicatePatient()
	async handlePatientValidation(
		params: {
			patient: PatientModel;
			patients: PatientModel[];
			feedObjects?: FeedObject[];
		},
		options?: IPatientValidationOptions
	) {
		const { patient } = params; // destructure

		/*
			If multiple patients in the queue is allowed (e.g. Link Siblings),
			then push it to the {@link patients} array rather than replacing
			the existing patient.
		 */
		if (options?.hasMultiplePatients) {
			this.patients.push(patient);
		} else {
			this.patients = [patient];
		}
		this.setScanOptions();
	}

	async setPatientInventory(patient: PatientModel, options?: any) {
		try {
			await this.base.presentLoading("Retrieving milk bottles");
			const milkBottles = await this.base.inventoryService.getMilkBottles(
				patient,
				options || {
					includeDischarged: false,
					includeUsed: false,
					includeExpended: false,
					includeExpired: false,
				}
			);
			this.inventory = milkBottles.sort((a, b) =>
				dayjs(a.expirationDate).isAfter(b.expirationDate) ? 1 : -1
			);
		} finally {
			this.setScanOptions();
			await this.base.dismissLoading();
		}
	}

	async handleRemoveButtonPressed(m: MilkBottleModel) {
		await this.base.alerts.presentAlert({
			header: "Remove From Queue",
			message: `Are you sure you want to remove milk with bottle number ${m.bottleNumber} from the queue?`,
			backdropDismiss: false,
			buttons: [
				{
					text: "Cancel",
					role: "cancel",
					htmlAttributes: { "data-testid": "alert_cancel_button" },
				},
				{
					text: "Yes",
					htmlAttributes: { "data-testid": "alert_yes_button" },
					handler: () => {
						this.handleRemoveScannedMilk(m);
						this.setScanOptions();
					},
				},
			],
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async handleModifierOptionPress(event: MilkModifierEvent) {
		console.warn("handleModifierOptionPress not implemented.");
	}

	/**
	 * @deprecated use {@link handleRemoveFeed}
	 */
	async handleRemoveProductButtonPress(m: MilkBankProductModel) {
		await this.base.alerts.presentAlert({
			header: "Remove From Queue",
			message: `Are you sure you want to remove milk with bottle number ${m.bottleNumber} from the queue?`,
			backdropDismiss: false,
			buttons: [
				{
					text: "Cancel",
					role: "cancel",
				},
				{
					text: "Yes",
					handler: () => {
						this.handleRemoveScannedProduct(m);
						this.setScanOptions();
					},
				},
			],
		});
	}

	/**
	 * @deprecated use {@link handleRemoveFeed}
	 * remove from current array
	 * remove from original array: deserialize, find item to remove, reserialize
	 */
	handleRemoveScannedMilk(milkBottle: MilkBottleModel) {
		this.milkBottles = this.milkBottles.filter(
			(m) => m.id !== milkBottle.id
		);
		this.scannedObjects = this.scannedObjects.filter(
			(x) => x.id !== milkBottle.id
		);

		this.originalMilks = this.originalMilks
			.map((o) => new MilkBottleModel(JSON.parse(o)))
			.filter((m) => m.id !== milkBottle.id)
			.map((m) => JSON.stringify(m));
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	handleRemoveScannedProduct(mbp?: MilkBankProductModel) {
		console.warn(
			`${this.constructor.name}.handleRemoveScannedMilk not implemented.`
		);
	}

	// Feed processing

	/**
	 * update and save scanned products. if "use all" don't print labels
	 * TODO: make this more easily testable
	 */
	async saveScannedProductChanges() {
		if (this.recipeData) {
			const milkBankProducts = this.recipeData.scanData.scannedProducts
				.map((s) => s.milkBankProduct)
				.filter((p) => p !== null);
			for (const milkBankProduct of milkBankProducts) {
				if (milkBankProduct.id) {
					const updated =
						await this.base.inventoryService.updateMilkBankProduct(
							milkBankProduct
						);

					if (!updated.expendedDate) {
						this.scannedProducts.push(updated);
					}
				}
			}
		} else {
			console.warn("saveScannedProductChanges: no recipe data");
		}
	}

	/**
	 * undo updates to scanned products
	 * TODO: make this more easily testable
	 */
	async restoreOriginalScannedProductState() {
		if (this.recipeData) {
			const originals = this.recipeData.scanData.scannedProducts
				.map((s) => s.originalMilkBankProduct)
				.filter((p) => p !== "null");

			console.log(JSON.stringify(originals, null, 2));
			for (const o of originals) {
				const milkBankProduct = new MilkBankProductModel(JSON.parse(o));
				console.log(JSON.stringify(milkBankProduct, null, 2));
				if (milkBankProduct.id) {
					await this.base.inventoryService.updateMilkBankProduct(
						milkBankProduct
					);
				}
			}
			this.scannedProducts = [];
			this.setScanOptions();
		}
	}

	async handleSelectPrinterPressed() {
		const modal = await this.base.modalCtrl.create({
			component: SelectItemModal,
			componentProps: {
				title: "Select Printer",
				items: StorageService.printers,
				searchProperties: {
					title: "name",
					subtitle: "",
					endText: "",
				},
			},
			cssClass: "fullscreen-modal",
		});

		modal.onDidDismiss().then((data) => {
			const selectedPrinter = data?.data;
			if (selectedPrinter) {
				this.selectedPrinter = selectedPrinter as PrinterModel;
				this.base.authService.updateUserProfile({
					currentPrinterId: selectedPrinter?.id,
					preferredTenantId: this.preferredTenant?.tenantId
				});
				StorageService.lastUsedCachedPrinter = selectedPrinter;

				console.log(
					`PrintLabelComponent.handleSelectPrinterPress onDidDismiss: ${JSON.stringify(
						selectedPrinter,
						null,
						2
					)}`
				);
			} else {
				console.log(
					"PrintLabelComponent.handleSelectPrinterPress onDidDismiss: no data passed"
				);
			}
			this.startScanListener();
		});

		return await modal.present().then(() => {
			this.stopScanListener();
		});
	}

	async handlePrinted() {
		await this.base.navigateRoot("/home");
	}

	// Second Nurse Validation

	/**
	 * Enabled + enforced = must scan badge only (no input text)
	 * Enabled + not enforced = can scan badge or input text
	 * Disabled + enforced = no badge scanning, input text only
	 * Disabled + not enforced = no badge scanning, input text only
	 *
	 * Used in FeedPatient and LinkSiblings
	 *
	 * The isOptionalToContinue option is used to determine whether a
	 * successful scan is required in order to dismiss the alert.
	 */
	async showSecondNurseValidation(
		workflowName: string,
		isOptionalToContinue: boolean,
		options?: {
			backdropDismiss?: boolean;
			buttons?: any[];
			hasScanOnDismiss: boolean;
		}
	) {
		const isEnabled = StorageService.appConfigs.enableBadgeScanning;
		const isEnforced = StorageService.configs.tenant.enforceBadgeScanning;
		const messageInstructions =
			this.base.nurseValidationService.getMessageInstructions({
				isEnabled,
				isEnforced,
				workflowName,
			});

		const submitButton = {
			text: "Submit",
			handler: async (data) => {
				this.stopScanListener();
				const nurseId = data[0];
				const result = nurseId
					? await this.handleNurseId(nurseId)
					: false;
				if (!result) {
					startRawScanListener();
				}
				return isOptionalToContinue || result;
			},
		};
		const scanButton = {
			text: "Scan",
			handler: async () => {
				this.scanningService
					.startScanbotScanner()
					.then(async (barcode) => {
						const nurseId = this.scanningService.processScanbotText(
							barcode?.items[0]?.barcode.text
						);
						const result = nurseId
							? await this.handleNurseId(nurseId)
							: false;
						AuthGuardService.appFocusGuard = true;
						return isOptionalToContinue || result;
					});
			},
		};

		// biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
		let buttons;
		if (isEnabled) {
			if (isEnforced) {
				buttons = this.isWeb ? [] : [scanButton];
			} else {
				buttons = this.isWeb
					? [submitButton]
					: [scanButton, submitButton];
			}
		} else {
			buttons = [submitButton];
		}

		const startRawScanListener = () => {
			this.scanningService
				.startRawScanListener()
				.pipe(filter((obj) => obj !== undefined)) // remove undefined
				.subscribe(async (nurseId) => {
					this.stopScanListener();
					const result = nurseId
						? await this.handleNurseId(nurseId)
						: false;
					startRawScanListener();
					if (isOptionalToContinue || result) {
						await this.base.alerts.dismissAlert();
					}
				});
		};

		if (isEnabled && this.isWeb) {
			startRawScanListener();
		}

		const alert = await this.base.alerts.createAlert({
			header: "Second Nurse Validation",
			message: messageInstructions,
			backdropDismiss: options?.backdropDismiss || false,

			// biome-ignore lint/correctness/noUnsafeOptionalChaining: <explanation>
			buttons: [...options?.buttons, ...buttons],
			inputs:
				isEnabled && isEnforced
					? []
					: [
							{
								placeholder: "Enter Nurse ID",
								attributes: {
									"data-testid": "nurse_id_input",
									autocomplete: "off",
								},
							},
						],
		});

		alert.onDidDismiss().then(() => {
			if (options?.hasScanOnDismiss) {
				this.startScanListener();
			} else {
				this.stopScanListener();
			}
		});

		alert.present().then(() => {
			// only when there is text field
			if (isEnabled && !isEnforced && this.isWeb) {
				const firstInput: any =
					document.querySelector("ion-alert input");
				firstInput.addEventListener("focus", () => {
					this.scanningService.stopScanListener();
				});
				firstInput.addEventListener("blur", () => {
					startRawScanListener();
				});
			}
			return;
		});
	}

	async handleNurseId(nurseId: string): Promise<boolean> {
		let promise: Promise<boolean>;
		try {
			await this.base.presentLoading("Validating nurse's ID...");

			const validatedNurse =
				await this.base.nurseValidationService.isAuthorizedNurse({
					nurseId,
				});

			this.handleNurseIdOnSuccess(validatedNurse);

			await this.base.dismissLoading();
			await this.base.modalService.presentVerified();
			promise = Promise.resolve(true);
		} catch (e) {
			await this.base.dismissLoading();

			const error = new ErrorModel(e.error.status, e.error.detail);
			await this.base.nurseValidationService.handleNurseValidationFailure(
				{
					error: error,
					workflow: this.workflow,
				}
			);

			promise = Promise.resolve(false);
		}
		return promise;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	handleNurseIdOnSuccess(nurse: Nurse) {
		console.warn("handleNurseIdOnSuccess not implemented");
	}

	// Alerts START

	async showRelabelProducts() {
		if (this.scannedProducts.length) {
			await this.base.alerts.showRelabelProducts();
		}
	}

	// Alerts END

	isDuplicate(milkBottle: MilkBottleModel): boolean {
		return this.milkBottles.some((m) => m.id === milkBottle.id);
	}

	@RejectRecalledProduct()
	@CheckPatientMatch()
	@RejectExpired()
	async handleNotReceivedDidDismiss(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
	}) {
		const { patient } = params;
		await this.base.modalService.presentVerified();

		const received = await this.base.inventoryService.receiveMilkBottle(
			params.milkBottle
		);

		this.feedObjects.push(received);
		this.originalFeedObjects.push(JSON.stringify(params.milkBottle));
		this.milkBottles.push(received);
		this.originalMilks.push(JSON.stringify(params.milkBottle));
		this.base.toastService.presentSuccessToast(
			`Received bottle #${params.milkBottle.bottleNumber}`
		);

		// Log match
		this.base.loggingService.logScan(
			new LogMilkBottleModel({
				type: ScanLogType.Milk_Bottle_Match,
				workflow: this.workflow,
				milkBottleId: params.milkBottle.id,
				patientId: patient.id,
			})
		);
	}

	@RejectRecalledProduct()
	@CheckPatientMatch()
	@RejectExpired()
	async handleInlineReceiveDidDismiss(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
	}): Promise<MilkBottleModel> {
		const { patient, milkBottle } = params;

		const received =
			await this.base.inventoryService.receiveMilkBottle(milkBottle);

		this.base.toastService.presentSuccessToast(
			`Received bottle #${milkBottle.bottleNumber}`
		);

		this.base.loggingService.logScan(
			new LogMilkBottleModel({
				type: ScanLogType.Milk_Bottle_Match,
				workflow: this.workflow,
				milkBottleId: milkBottle.id,
				patientId: patient.id,
			})
		);

		return received;
	}

	async presentEditMilkModal(params: {
		milkBottle?: MilkBottleModel;
		milkBankProduct?: MilkBankProductModel;
		presetState?: FeedState;
		title?: string;
		header?: string;
		message?: string;
		doneText?: string;
	}) {
		const modal = await this.base.modalService.presentModal({
			component: EditMilkModal,
			cssClass: ModalType.NotReceived,
			componentProps: {
				milkBottle: params.milkBottle,
				milkBankProduct: params.milkBankProduct,
				presetState: params.presetState,
				title: params.title,
				header: params.header,
				message: params.message,
				doneText: params.doneText,
			},
		});

		modal.onDidDismiss().then((data) => {
			this.startScanListener();
			// TODO
			console.log(JSON.stringify(data, null, 2));
		});

		return await modal.present().then(() => {
			this.stopScanListener();
			this.base.dismissLoading();
		});
	}

	async presentNotReceivedModal2(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
		presetMilkState?: MilkState;
	}) {
		const modal = await this.base.modalService.presentModal({
			component: EditMilkModal,
			cssClass: ModalType.NotReceived,
			componentProps: {
				title: "Not Received",
				header: "Receive Milk",
				message:
					"Milk label has not been received yet. Please specify milk state and pump/thawed date.",
				milkBottle: params.milkBottle,
				presetMilkState: params.presetMilkState,
				hasEditableVolume: true,
				doneText: "Done",
			},
		});

		modal.onDidDismiss().then((data) => {
			console.log(JSON.stringify(data, null, 2));
			if (data.data) {
				try {
					this.handleNotReceivedDidDismiss({
						patient: params.patient,
						milkBottle: data.data.milkBottle,
					});
				} catch (error) {
					this.handleMilkBottleScanValidationError({
						error,
						milkBottle: params.milkBottle,
					});
				}
			} else {
				console.log(
					`${this.constructor.name}.presentNotReceivedModal2 onDidDismiss: no data passed`
				);
			}

			this.startScanListener();
		});

		return await modal.present().then(() => {
			this.stopScanListener();
			this.base.dismissLoading();
		});
	}

	/**
	 * Presents an inline modal for receiving a milk bottle and handles the user's input.
	 *
	 * @param params - The parameters required to present the modal.
	 * @param params.patient - The patient associated with the milk bottle.
	 * @param params.milkBottle - The milk bottle to be received.
	 * @param params.presetMilkState - (Optional) The preselected state of the milk.
	 * @returns A promise that resolves to the updated `MilkBottleModel` after the modal is dismissed.
	 *
	 * The modal allows the user to specify the milk state and pump/thawed date for a milk bottle
	 * that has not yet been received. If the user provides updated data, it is processed and returned.
	 * If the user cancels the modal, the original milk bottle is returned.
	 */
	async presentInlineReceiveModal(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
		presetMilkState?: MilkState;
	}): Promise<MilkBottleModel> {
		const modal = await this.base.modalService.presentModal({
			component: EditMilkModal,
			cssClass: ModalType.NotReceived,
			componentProps: {
				title: "Not Received",
				header: "Receive Milk",
				message:
					"This bottle hasn’t been received yet. Please specify milk state and pump/thawed date.",
				milkBottle: params.milkBottle,
				presetMilkState: params.presetMilkState,
				hasEditableVolume: true,
				doneText: "Done",
			},
		});

		await modal.present();
		this.stopScanListener();
		this.base.dismissLoading();

		// wait for the user to close the modal and return data
		const { data } = await modal.onDidDismiss<{
			milkBottle: MilkBottleModel;
		}>();

		if (!data) {
			console.log(
				"presentInlineReceiveModal onDidDismiss: no data passed"
			);
			this.startScanListener();
			return params.milkBottle;
		}

		this.startScanListener();

		return await this.handleInlineReceiveDidDismiss({
			patient: params.patient,
			milkBottle: data.milkBottle,
		});
	}

	/**
	 * Presents a modal for handling the re-receiving of a previously discharged milk bottle.
	 *
	 * This method displays a modal dialog where the user can verify the state and volume of
	 * a milk bottle that was previously discharged. The user can make adjustments and confirm
	 * the re-receipt of the milk bottle. The discharge date of the milk bottle will be reset
	 * to `null` upon successful re-receipt.
	 *
	 * @param params.patient - The patient associated with the milk bottle.
	 * @param params.milkBottle - The milk bottle to be re-received.
	 * @param params.presetMilkState - (Optional) The preselected state of the milk bottle.
	 *
	 * @returns A promise that resolves to the updated `MilkBottleModel` after the modal is dismissed.
	 *
	 * If the user cancels the modal, the original milk bottle is returned.
	 */
	async presentPreviouslyDischargedInlineReceiveModal(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
		presetMilkState?: MilkState;
	}): Promise<MilkBottleModel> {
		const modal = await this.base.modalService.presentModal({
			component: EditMilkModal,
			cssClass: ModalType.NotReceived,
			componentProps: {
				title: "Previously Discharged",
				header: "Receive Milk",
				message:
					"This milk was previously discharged. Please verify the state and volume to re-receive.",
				milkBottle: params.milkBottle,
				presetMilkState: params.presetMilkState,
				hasEditableVolume: true,
				doneText: "Done",
			},
		});

		await modal.present();
		this.stopScanListener();
		this.base.dismissLoading();

		// wait for the user to close the modal and return data
		const { data } = await modal.onDidDismiss<{
			milkBottle: MilkBottleModel;
		}>();

		if (!data) {
			console.log(
				"presentPreviouslyDischargedInlineReceiveModal onDidDismiss: no data passed"
			);
			this.startScanListener();
			return params.milkBottle;
		}

		this.startScanListener();
		data.milkBottle.dischargeDate = null; // Set the discharge date to null to re-receive the milk

		return await this.handleInlineReceiveDidDismiss({
			patient: params.patient,
			milkBottle: data.milkBottle,
		});
	}

	async presentPreviouslyDischargedModal(params: {
		patient: PatientModel;
		milkBottle: MilkBottleModel;
		presetMilkState?: MilkState;
	}) {
		const modal = await this.base.modalService.presentModal({
			component: EditMilkModal,
			cssClass: ModalType.NotReceived,
			componentProps: {
				title: "Previously Discharged",
				header: "Receive Milk",
				message:
					"This milk was previously discharged. Please verify the state and volume to re-receive.",
				milkBottle: params.milkBottle,
				presetMilkState: params.presetMilkState,
				hasEditableVolume: true,
				doneText: "Done",
			},
		});

		modal.onDidDismiss().then((data) => {
			console.log(JSON.stringify(data, null, 2));
			if (data.data) {
				// Set the discharge date to null to re-receive the milk
				data.data.milkBottle.dischargeDate = null;
				try {
					this.handleNotReceivedDidDismiss({
						patient: params.patient,
						milkBottle: data.data.milkBottle,
					});
				} catch (error) {
					this.handleMilkBottleScanValidationError({
						error,
						milkBottle: params.milkBottle,
					});
				}
			} else {
				console.log(
					`${this.constructor.name}.presentNotReceivedModal2 onDidDismiss: no data passed`
				);
			}
			this.startScanListener();
		});

		return await modal.present().then(() => {
			this.base.dismissLoading();
		});
	}

	async presentSiblingSelectAlert(patient: PatientModel) {
		this.assignedPatients = [];

		if (patient.siblings.length === 0) {
			return;
		}

		const checkboxOptions: AlertInput[] = [];
		const primaryPatientOption: AlertInput = {
			name: "checkbox1",
			type: "checkbox",
			label: `${patient.lastName}, ${patient.firstName} (${patient.mrn})`,
			value: `${JSON.stringify(patient)}`,
			checked: true,
		};
		checkboxOptions.push(primaryPatientOption);

		for (const [i, sibling] of patient.siblings.entries()) {
			const siblingOption: AlertInput = {
				name: `checkbox${i + 2}`, // i starts at 0, and selected baby is index radio1
				type: "checkbox",
				label: `${sibling.lastName}, ${sibling.firstName} (${sibling.mrn})`,
				value: `${JSON.stringify(sibling)}`,
			};
			checkboxOptions.push(siblingOption);
		}

		const alert = await this.base.alerts.createAlert({
			header: "Siblings Detected",
			message:
				"Select all siblings you would like to assign this milk for",
			inputs: checkboxOptions,
			backdropDismiss: false,
			buttons: [
				{
					text: "Select",
					htmlAttributes: {
						"data-testid": "alert_sibling_select_button",
					},
					handler: async (values: string[]) => {
						this.assignedPatients = [];
						for (const str of values) {
							this.assignedPatients.push(JSON.parse(str));
						}
					},
				},
			],
		});

		await alert.present();

		/*
		 	TODO: not sure if this is needed. If it is, then every page needs to have
		 	 @ViewChild(IonContent) content: IonContent = {} as any;
		 	 For now, place in try/catch to fail gracefully.
		 */
		await alert.onDidDismiss().then(() => {
			try {
				this.scrollToTop();
			} catch (e) {
				console.warn(
					"scrollToTop() failed. No content object defined on this page"
				);
			}
		});
	}

	/*
	 Recipe Modal
	 */

	// TODO: make reusable and move here?

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	handleRecipeModal2(recipeData: RecipeData) {
		console.warn(
			`${this.constructor.name}.handleRecipeModal not implemented.`
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async print(data: { selectedPrinter: any; numberOfBottles: number }) {
		console.warn(`${this.constructor.name}.print not implemented.`);
	}

	/**
	 * @deprecated use {@link isSelectDisabled2} once the queue is stored in
	 * feedObjects rather than milkBottles or milkBankProducts
	 */
	isSelectDisabled(): boolean {
		return !!(this.patients.length && this.milkBottles.length > 0);
	}

	/**
	 * User Story:
	 * As a clinician, I want to be prevented from changing a patient once a patient and at least one milk bank product has been scanned or selected, so that I must restart the entire workflow from the beginning in order to prevent accidentally changing the patient.
	 */
	isSelectPatientDisabled(
		patients: PatientModel[],
		feedObjects: FeedObject[]
	): boolean {
		return !!(patients.length && feedObjects.length);
	}

	/**
	 * Once a feed object with a patient is selected, the select patient button
	 * should be disabled.
	 *
	 * TODO: In the future, we can check if the feedObject inherently has a
	 *  patient tied to it (i.e. an assigned feed).
	 */
	isSelectDisabled2(): boolean {
		return !!(this.patients.length && this.feedObjects.length > 0);
	}

	displayDate(date: dayjs.Dayjs): string {
		return displayDate(date);
	}

	// Debug stuff
	async handleDebugSelectMilkPress(
		feedType?: "milkbottle" | "milkbankproduct"
	) {
		const [patient] = this.patients;
		await this.showDebugSelectMilkModal(patient, feedType);
	}

	async showDebugSelectMilkModal(
		patient: PatientModel,
		feedType?: "milkbottle" | "milkbankproduct"
	) {
		const modal = await this.base.modalService.presentModal({
			component: DebugSelectMilkModal,
			cssClass: ModalType.SelectPatientList,
			componentProps: {
				patient,
				feedType,
			},
		});

		modal.onDidDismiss().then(async (data) => {
			if (!data.data) {
				console.log(
					`${this.constructor.name}.showDebugSelectMilkModal onDidDismiss: no data passed`
				);
			} else {
				const barcodeText = data.data;
				const o = this.scanningService.getScannedObject(barcodeText);
				await this.handleScannedObject(o);
			}

			this.startScanListener();
		});

		return await modal.present().then(() => {
			this.stopScanListener();
		});
	}

	/**
	 * The way scanned feed validation is handled is by displaying a modal.
	 * However if the error type is BottleNotReceivedError, we need to display a
	 * different modal.
	 *
	 * TODO: Make sure this works with Milk Room Prep
	 */

	async handleMilkBottleScanValidationError(params: {
		error: ScanMilkBottleValidationError | BottleNotReceivedError;
		milkBottle: MilkBottleModel;
		patient?: PatientModel;
		isOverrideNotReceivedError?: boolean;
	}) {
		const patient = params.patient || this.patients[0];
		if (params.error instanceof ScanMilkBottleValidationError) {
			// display the associated modal
			const { header, message, modalType } = params.error.modal;
			await this.base.modalService.presentOverlayModal(
				modalType,
				header,
				[message]
			);
		}

		// TODO: ML-1671
		if (
			params.error instanceof BottleNotReceivedError &&
			!params.isOverrideNotReceivedError
		) {
			this.stopScanListener();
			const previouslyDischarged = isDischarged(params.milkBottle);
			if (previouslyDischarged) {
				this.presentPreviouslyDischargedModal({
					patient,
					milkBottle: params.milkBottle,
					presetMilkState: params.error.presetMilkState,
				});
			} else {
				this.presentNotReceivedModal2({
					patient,
					milkBottle: params.milkBottle,
					presetMilkState: params.error.presetMilkState,
				});
			}
		}

		// Regardless of the error type, we'll log.
		this.base.loggingService.logScan(
			new LogMilkBottleModel({
				type: params.error.scanLogType,
				workflow: this.workflow,
				milkBottleId: params.milkBottle.id,
				patientId: patient?.id,
			})
		);
	}

	async handleMilkBankProductScanValidationError(params: {
		error: ScanMilkBankProductValidationError;
		milkBankProduct: MilkBankProductModel;
		patient?: PatientModel;
	}) {
		const patient = params.patient || this.patients[0];
		if (params.error instanceof ScanMilkBankProductValidationError) {
			// display the associated modal
			const { header, message, modalType } = params.error.modal;
			await this.base.modalService.presentOverlayModal(
				modalType,
				header,
				[message]
			);
		}

		// Regardless of the error type, we'll log.
		await this.base.loggingService.logScan(
			new LogMilkBankProductModel({
				type: params.error.scanLogType,
				workflow: this.workflow,
				milkBankProductId: params.milkBankProduct?.id,
				patientId: patient?.id,
			})
		);
	}

	async handleScannedObjectValidationError(params: {
		error: ScannedObjectError;
		scannedObject: ScannedObject;
	}) {
		// display the associated modal
		const { header, message, modalType } = params.error.modal;
		await this.base.modalService.presentOverlayModal(modalType, header, [
			message,
		]);

		this.base.loggingService.logScan(
			new LogScannedObjectModel({
				type: params.error.scanLogType,
				workflow: this.workflow,
				productId: params.scannedObject.product.id,
			})
		);
	}

	async handlePatientValidationError(params: {
		error: ScannedPatientError | ScannedWristbandError;
	}) {
		const { error } = params;
		if (error instanceof ScannedPatientError) {
			const mismatchedPatient = await this.base.patientService.getPatient(
				`?mrn=${error.mrn}`
			);
			const patient = error.patient;

			// display the associated modal
			const { header, message, modalType } =
				modalMessage.scan_patient_mismatch(mismatchedPatient, patient);

			await this.base.modalService.presentOverlayModal(
				modalType,
				header,
				[message]
			);
		} else if (error instanceof ScannedWristbandError) {
			const { modal } = error as ScannedWristbandError;

			await this.base.modalService.presentOverlayModal(
				modal.modalType,
				modal.header,
				[modal.message]
			);
		}

		this.base.loggingService.logScan(
			new LogAttemptModel({
				type: error.scanLogType,
				workflow: this.workflow,
			})
		);
	}
}
export interface ScanOptions {
	isWeb: boolean;
	currentStep: boolean;
	items: { title: ScanLabelTitle; isVisible: boolean }[];
}

export interface IPatientValidationOptions {
	hasMultiplePatients?: boolean;
}
